// @flow
import _ from 'underscore';

export default class Team {

  id: string;
  name: string;
  nick: string;
  leagueId: string;
  division: string;
  conference: string;
  links: Object;
  record: Object;
  colors: Object;

  constructor({ id, name, nick, leagueId, division, conference, links, record, colorPrimary, colorSecondary, abrv }) {
    this.id = id;
    this.name = name;
    this.nick = nick;
    this.division = division;
    this.conference = conference;
    this.links = links;
    this.record = record || {};
    this.colors = { primary: colorPrimary || '000', secondary: colorSecondary || '000' };
    this.leagueId = leagueId;
    this.abrv = abrv;
  }

  getConference() {
    return this.conference;
  };

  getDivision() {
    return this.division;
  };

  getFullName() {
    return this.hasUniqueNameAndNick() && this.hasNick() ? `${this.getName()} ${this.getNick()}` : this.getName();
  };

  getId() {
    return this.id;
  };

  getLogoHref() {
    return _.chain(this.links)
      .findWhere({ rel: 'logo' })
      .pick('href')
      .values()
      .first()
      .value();
  }

  getName(longestNameLength) {
    const name = this.name ? this.name : '';
    return name.length > longestNameLength ? this.abrv : name;
  };

  getAbreviation() {
    return this.abrv;
  };

  getNick() {
    return this.nick;
  };

  getShortestName() {
    return (this.name.length <= this.nick.length ? this.name : this.nick);
  };

  getOverallRecord() {
    return `${this.getOverallWins()}-${this.getOverallLosses()}`;
  };

  getConferenceRecord() {
    return `${this.getConferenceWins()}-${this.getConferenceLosses()}`;
  };

  getOverallWins() {
    return this.record.overall ? this.record.overall.wins : 0;
  };

  getOverallLosses() {
    return this.record.overall ? this.record.overall.losses : 0;
  };

  getConferenceWins() {
    return (this.record.conference ? this.record.conference.wins || 0 : 0);
  };

  getConferenceLosses() {
    return (this.record.conference ? this.record.conference.losses || 0 : 0);
  };

  getHomeWins() {
    return (this.record.home ? this.record.home.wins || 0 : 0);
  };

  getHomeLosses() {
    return (this.record.home ? this.record.home.losses || 0 : 0);
  };

  getHomeRecord() {
    return `${this.getHomeWins()}-${this.getHomeLosses()}`;
  };

  getAwayWins() {
    return (this.record.away ? this.record.away.wins || 0 : 0);
  };

  getAwayLosses() {
    return (this.record.away ? this.record.away.losses || 0 : 0);
  };

  getAwayRecord() {
    return `${this.getAwayWins()}-${this.getAwayLosses()}`;
  };

  getNeutralWins() {
    return this.getOverallWins() - this.getHomeWins() - this.getAwayWins();
  };

  getNeutralLosses() {
    return this.getOverallLosses() - this.getHomeLosses() - this.getAwayLosses();
  };

  getNeutralRecord() {
    return `${this.getNeutralWins()}-${this.getNeutralLosses()}`;
  };

  getPrimaryColor() {
    return this.colors.primary || '000000';
  };

  getSecondaryColor() {
    return this.colors.secondary || 'FFFFFF';
  };

  hasUniqueNameAndNick() {
    return this.name && this.name !== this.nick;
  };

  hasNick() {
    return !!this.nick;
  };

  isEqualTo = (team: Team) => {
    return (team && typeof team.getId === 'function') ? team.getId() === this.getId() : false;
  };

  isDiv1() {
    return Boolean(this.getConference());
  };

  isUnknownLeague() {
    return [ 'NCAAB-U', 'WNCAAB-U' ].includes(this.leagueId);
  }

  matchesString(substring: string) {
    if (typeof substring !== 'string') {
      return false;
    } else {
      return this.getFullName().toLowerCase().indexOf(substring.toLowerCase()) > -1;
    }
  };
}